import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Building2 } from "lucide-react";
import { Badge, Button, DetailPage, Field, Input, Section, Select } from "@redipos/ui";
import type { ConsolePlanApi, ConsoleSubscriptionPaymentApi, ConsoleTenantDetail } from "@redipos/contracts";
import { finanzasApi } from "../api.js";

function errMsg(e: unknown, fallback = "Error") {
  return e instanceof Error ? e.message : fallback;
}

const STATUS_OPTS = [
  { value: "trial", label: "Prueba" },
  { value: "active", label: "Activo" },
  { value: "past_due", label: "Pago pendiente" },
  { value: "cancelled", label: "Cancelado" },
  { value: "expired", label: "Vencido" },
];

const METHOD_OPTS = [
  { value: "transfer", label: "Transferencia" },
  { value: "cash", label: "Efectivo" },
  { value: "card", label: "Tarjeta" },
  { value: "other", label: "Otro" },
];

const BILLING_REASON_LABEL: Record<string, string> = {
  ok: "Al día",
  payment_grace: "Pago pendiente en gracia",
  payment_overdue: "Suspendida por falta de pago",
  cancelled_grace: "Cancelada en gracia",
  cancelled_overdue: "Suspendida por cancelación",
  expired: "Vencida",
};

function formatDate(value: unknown) {
  if (!value) return "—";
  return new Date(value as string | number | Date).toLocaleDateString("es-PY");
}

function billingVariant(reason: unknown) {
  if (reason === "payment_overdue" || reason === "cancelled_overdue" || reason === "expired") return "danger";
  if (reason === "payment_grace" || reason === "cancelled_grace") return "warning";
  return "success";
}

/**
 * Billing de un tenant — Finanzas. La ficha operativa (organización,
 * usuarios, impersonación) vive en Operaciones (`/clientes/:id`). Separadas
 * a propósito: ver docs/analisis/consola-admin-rediseno.md §4.1/§4.2.
 */
export default function TenantBillingPage() {
  const { id } = useParams();
  const [tenant, setTenant] = useState<ConsoleTenantDetail | null>(null);
  const [plans, setPlans] = useState<ConsolePlanApi[]>([]);
  const [payments, setPayments] = useState<ConsoleSubscriptionPaymentApi[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const [planId, setPlanId] = useState("");
  const [subStatus, setSubStatus] = useState("active");
  const [savingPlan, setSavingPlan] = useState(false);

  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payMethod, setPayMethod] = useState("transfer");
  const [payRef, setPayRef] = useState("");
  const [payNote, setPayNote] = useState("");
  const [savingPay, setSavingPay] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    const tid = id;
    if (!tid) {
      setLoading(false);
      setErr("Falta el identificador del cliente.");
      return;
    }
    try {
      const [t, p, pay] = await Promise.all([
        finanzasApi.tenant(tid),
        finanzasApi.plans(),
        finanzasApi.tenantPayments(tid),
      ]);
      setTenant(t);
      setPlans(p);
      setPayments(pay);
      setPlanId(t.subscription?.planId || "");
      setSubStatus(t.subscription?.status || "active");
    } catch (e) {
      setErr(errMsg(e, "Error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const savePlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant || !planId) return;
    setSavingPlan(true);
    setErr(null);
    try {
      await finanzasApi.updateTenantPlan(tenant.id, { planId, status: subStatus });
      await load();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setSavingPlan(false);
    }
  };

  const addPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant) return;
    setSavingPay(true);
    setErr(null);
    try {
      await finanzasApi.createPayment(tenant.id, {
        amount: payAmount,
        paidAt: new Date(payDate + "T12:00:00").toISOString(),
        method: payMethod,
        reference: payRef || undefined,
        note: payNote || undefined,
        subscriptionId: tenant.subscription?.id || undefined,
      });
      setPayAmount("");
      setPayRef("");
      setPayNote("");
      await load();
    } catch (e2) {
      setErr(errMsg(e2));
    } finally {
      setSavingPay(false);
    }
  };

  if (loading && !tenant) {
    return <p className="text-fg-subtle text-sm py-12 text-center">Cargando…</p>;
  }
  if (err && !tenant) {
    return (
      <div className="space-y-4">
        <p className="text-danger">{err}</p>
        <Link to="/clientes" className="text-brand-500 text-sm">
          ← Volver
        </Link>
      </div>
    );
  }
  if (!tenant) return null;

  const billing = tenant.subscription?.billing;
  const metrics = [
    { key: "plan", label: "Plan actual", value: tenant.subscription?.plan?.name || "Sin plan" },
    { key: "estado", label: "Estado suscripción", value: tenant.subscription?.status || "—" },
    { key: "pagos", label: "Pagos registrados", value: payments.length },
  ];

  return (
    <DetailPage
      title={`Facturación · ${tenant.name}`}
      subtitle={tenant.id}
      metrics={metrics}
      actions={
        <Link to={`/clientes/${tenant.id}`}>
          <Button type="button" variant="secondary">
            <Building2 className="w-4 h-4" />
            Ver ficha de cliente
          </Button>
        </Link>
      }
      shellSpacing="lg"
    >
      <Link to="/clientes" className="inline-flex items-center gap-1 text-sm text-fg-subtle hover:text-fg-muted">
        <ArrowLeft className="w-4 h-4" />
        Clientes
      </Link>

      {err && (
        <p className="text-sm text-danger rounded-input border border-danger/40 bg-danger-soft px-3 py-2">{err}</p>
      )}

      <form onSubmit={savePlan} className="space-y-4">
        <Section title="Suscripción">
          <Field label="Plan">
            <Select value={planId} onChange={(e) => setPlanId(e.target.value)}>
              <option value="">— Seleccionar —</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.currency} {Number(p.price).toLocaleString("es-PY")})
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Estado suscripción">
            <Select value={subStatus} onChange={(e) => setSubStatus(e.target.value)}>
              {STATUS_OPTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          {billing && (
            <div className="rounded-input border border-border bg-surface-muted p-3 text-sm space-y-2">
              <div className="flex items-center justify-between gap-3">
                <span className="text-fg-subtle">Estado billing</span>
                <Badge variant={billingVariant(billing.reason)}>
                  {BILLING_REASON_LABEL[String(billing.reason)] || billing.reason}
                </Badge>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-fg-subtle">Gracia hasta</span>
                <span className="text-fg">{formatDate(billing.graceEndsAt)}</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-fg-subtle">Días restantes</span>
                <span className="text-fg">
                  {typeof billing.graceDaysRemaining === "number" ? billing.graceDaysRemaining : "—"}
                </span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-fg-subtle">Último pago</span>
                <span className="text-fg">{formatDate(billing.lastPaymentAt)}</span>
              </div>
            </div>
          )}
          <Button type="submit" disabled={!planId} loading={savingPlan} loadingLabel="Guardando…" className="w-full">
            Guardar suscripción
          </Button>
        </Section>
      </form>

      <Section title="Pagos registrados">
        <p className="text-xs text-fg-subtle -mt-2">
          Registro manual (transferencia, efectivo, etc.). No reemplaza la facturación interna del cliente.
        </p>
        <form onSubmit={addPayment} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          <Field label="Monto" required>
            <Input
              type="text"
              inputMode="decimal"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              placeholder="0"
              required
            />
          </Field>
          <Field label="Fecha pago" required>
            <Input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)} required />
          </Field>
          <Field label="Medio">
            <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
              {METHOD_OPTS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
          </Field>
          <div className="sm:col-span-2 lg:col-span-3">
            <Field label="Referencia / comprobante">
              <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} />
            </Field>
          </div>
          <div className="sm:col-span-2 lg:col-span-6">
            <Field label="Nota">
              <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} />
            </Field>
          </div>
          <div className="sm:col-span-2 lg:col-span-6">
            <Button type="submit" variant="secondary" loading={savingPay} loadingLabel="Registrando…">
              Registrar pago
            </Button>
          </div>
        </form>

        <div className="rounded-input border border-border overflow-hidden">
          <div className="table-x-scroll max-w-full">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-fg-subtle text-xs uppercase bg-surface-elevated">
                  <th className="px-3 py-2 font-medium">Fecha</th>
                  <th className="px-3 py-2 font-medium">Monto</th>
                  <th className="px-3 py-2 font-medium">Medio</th>
                  <th className="px-3 py-2 font-medium">Referencia</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-fg-subtle">
                      Sin pagos registrados
                    </td>
                  </tr>
                ) : (
                  payments.map((row) => (
                    <tr key={row.id} className="text-fg-muted">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {new Date(row.paidAt).toLocaleDateString("es-PY")}
                      </td>
                      <td className="px-3 py-2 font-medium tabular-nums text-fg">
                        {row.currency} {String(row.amount)}
                      </td>
                      <td className="px-3 py-2">{row.method}</td>
                      <td className="px-3 py-2 text-fg-subtle truncate max-w-[200px]">{row.reference || "—"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Section>
    </DetailPage>
  );
}
